// authHelpers.js
import { CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import userPool from "./cognitoConfig";

// Constants
export const COGNITO_DOMAIN = "<YOUR_COGNITO_DOMAIN>";
export const COGNITO_CLIENT_ID = "<YOUR_CLIENT_ID>";
export const REDIRECT_URI = "<YOUR_REDIRECT_URI>";
export const PKCE_VERIFIER_KEY = "pkce_verifier";

// ---------------------------
// EMAIL / PASSWORD FLOW
// ---------------------------
export const signUp = (email, password) => {
  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, [], null, (err, result) => {
      if (err) reject(new Error(`Sign Up Error: ${err.message}`));
      else {
        localStorage.setItem("email", email);
        localStorage.setItem("password", password);
        resolve(result.user);
      }
    });
  });
};

export const confirmSignUp = (email, code) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmRegistration(code, true, (err, result) => {
      if (err) reject(new Error(`Confirmation Error: ${err.message}`));
      else resolve(result);
    });
  });
};

export const signIn = (email, password) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        localStorage.setItem("idToken", session.getIdToken().getJwtToken());
        localStorage.setItem("accessToken", session.getAccessToken().getJwtToken());
        localStorage.setItem("refreshToken", session.getRefreshToken().getToken());
        resolve(session);
      },
      onFailure: (err) => reject(new Error(err.message || "Sign In Failed")),
    });
  });
};

// Forgot Password
export const forgotPassword = (email) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.forgotPassword({
      onSuccess: (data) => resolve(data),
      onFailure: (err) => reject(new Error(`Forgot Password Error: ${err.message}`)),
    });
  });
};

export const confirmNewPassword = (email, code, newPassword) => {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve("Password reset successful"),
      onFailure: (err) => reject(new Error(`Password Reset Failed: ${err.message}`)),
    });
  });
};

// Get Session
export const getSession = () => {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) return reject(new Error("No user found"));
    user.getSession((err, session) => {
      if (err) reject(new Error(`Session error: ${err.message}`));
      else resolve(session);
    });
  });
};

// ---------------------------
// GOOGLE SSO FLOW
// ---------------------------
export const handleGoogleSSO = () => {
  const codeVerifier = generateRandomString(128);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  const codeChallenge = base64URLEncode(sha256(codeVerifier));

  const state = generateRandomString(16);

  const ssoUrl = `${COGNITO_DOMAIN}/oauth2/authorize?response_type=code&client_id=${COGNITO_CLIENT_ID}&redirect_uri=${encodeURIComponent(
    REDIRECT_URI
  )}&state=${state}&code_challenge_method=S256&code_challenge=${codeChallenge}&identity_provider=Google`;

  window.location.href = ssoUrl;
};

// API fetch helper
export const apiFetch = (url, options = {}) => {
  const token = localStorage.getItem("idToken");
  return fetch(url, {
    ...options,
    headers: { ...options.headers, Authorization: `Bearer ${token}` },
  });
};

// ---------------------------
// HELPERS
// ---------------------------
function generateRandomString(length) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  let str = "";
  for (let i = 0; i < length; i++) str += chars.charAt(Math.floor(Math.random() * chars.length));
  return str;
}

function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest("SHA-256", data);
}

function base64URLEncode(bufferPromise) {
  return bufferPromise.then((buffer) => {
    const bytes = new Uint8Array(buffer);
    let str = "";
    for (let b of bytes) str += String.fromCharCode(b);
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  });
}
